import React, { useState, useRef } from 'react';
import {
  VStack,
  Input,
  Select,
  Textarea,
  Text,
  Box,
  Button,
  SimpleGrid,
  FormLabel,
  FormControl,
  HStack,
  Badge,
  Icon,
  useToast,
} from '@chakra-ui/react';
import { FiUpload, FiFile } from 'react-icons/fi';
import axiosInstance from '../../../api/AxiosInstance';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: any;
  onGuardado?: () => void;
  onCancelar?: () => void;
}

const ESTATUS_COLORS: Record<string, string> = {
  Borrador: 'gray',
  Activo: 'green',
  Concluido: 'blue',
};

const FormularioPlanCultura: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [anio, setAnio] = useState<string>(data?.anio?.toString() || new Date().getFullYear().toString());
  const [nombrePlan, setNombrePlan] = useState<string>(data?.nombrePlan || '');
  const [objetivoGeneral, setObjetivoGeneral] = useState<string>(data?.objetivoGeneral || '');
  const [descripcion, setDescripcion] = useState<string>(data?.descripcion || '');
  const [fechaInicio, setFechaInicio] = useState<string>(
    data?.fechaInicio ? data.fechaInicio.split('T')[0] : ''
  );
  const [fechaFin, setFechaFin] = useState<string>(
    data?.fechaFin ? data.fechaFin.split('T')[0] : ''
  );
  const [responsable, setResponsable] = useState<string>(data?.responsable || '');
  const [estatus, setEstatus] = useState<string>(data?.estatus || 'Borrador');
  const [porcentajeAvance, setPorcentajeAvance] = useState<string>(
    data?.porcentajeAvance?.toString() || '0'
  );
  const [archivoPdf, setArchivoPdf] = useState<File | null>(null);
  const [nombreArchivoActual, setNombreArchivoActual] = useState<string>(
    data?.rutaDocumento || ''
  );
  const [guardando, setGuardando] = useState(false);

  const aniosDisponibles = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 2 + i);

  const handleSeleccionarArchivo = () => {
    fileInputRef.current?.click();
  };

  const handleCambioArchivo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const archivo = e.target.files?.[0];
    if (archivo) {
      if (archivo.type !== 'application/pdf') {
        toast({
          title: 'Formato inválido',
          description: 'Solo se permiten archivos PDF.',
          status: 'warning',
          duration: 3000,
        });
        return;
      }
      setArchivoPdf(archivo);
      setNombreArchivoActual(archivo.name);
    }
  };

  const handleGuardar = async () => {
    if (!anio || !nombrePlan || !fechaInicio || !fechaFin || !responsable) {
      toast({
        title: 'Campos obligatorios',
        description: 'Completa todos los campos requeridos.',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (new Date(fechaFin) < new Date(fechaInicio)) {
      toast({
        title: 'Fechas inválidas',
        description: 'La fecha fin no puede ser anterior a la fecha inicio.',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setGuardando(true);
    try {
      const formData = new FormData();
      formData.append('anio', anio);
      formData.append('nombrePlan', nombrePlan);
      formData.append('objetivoGeneral', objetivoGeneral);
      formData.append('descripcion', descripcion);
      formData.append('fechaInicio', fechaInicio);
      formData.append('fechaFin', fechaFin);
      formData.append('responsable', responsable);
      formData.append('estatus', estatus);
      formData.append('porcentajeAvance', porcentajeAvance);
      if (archivoPdf) {
        formData.append('documento', archivoPdf);
      }

      if (modo === 'edit') {
        await axiosInstance.put(`/cultura/plan-anual/${data.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await axiosInstance.post('/cultura/plan-anual', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      toast({
        title: modo === 'edit' ? 'Plan actualizado' : 'Plan registrado',
        description: modo === 'edit' ? 'El plan fue actualizado exitosamente.' : 'El plan fue registrado exitosamente.',
        status: 'success',
        duration: 3000,
      });

      onGuardado?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Error al guardar el plan.',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setGuardando(false);
    }
  };

  // Modo vista (solo lectura)
  if (modo === 'view') {
    return (
      <VStack spacing={4} align="stretch">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <Box>
            <Text fontSize="xs" color="gray.500" fontWeight="bold">AÑO</Text>
            <Text fontWeight="semibold">{data?.anio}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500" fontWeight="bold">ESTATUS</Text>
            <Badge colorScheme={ESTATUS_COLORS[data?.estatus] || 'gray'} fontSize="sm" px={3} py={1} borderRadius="full">
              {data?.estatus}
            </Badge>
          </Box>
          <Box gridColumn={{ md: 'span 2' }}>
            <Text fontSize="xs" color="gray.500" fontWeight="bold">NOMBRE DEL PLAN</Text>
            <Text fontWeight="semibold">{data?.nombrePlan}</Text>
          </Box>
          <Box gridColumn={{ md: 'span 2' }}>
            <Text fontSize="xs" color="gray.500" fontWeight="bold">OBJETIVO GENERAL</Text>
            <Text>{data?.objetivoGeneral || '—'}</Text>
          </Box>
          <Box gridColumn={{ md: 'span 2' }}>
            <Text fontSize="xs" color="gray.500" fontWeight="bold">DESCRIPCIÓN</Text>
            <Text>{data?.descripcion || '—'}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500" fontWeight="bold">FECHA INICIO</Text>
            <Text>{data?.fechaInicio ? new Date(data.fechaInicio).toLocaleDateString('es-MX') : '—'}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500" fontWeight="bold">FECHA FIN</Text>
            <Text>{data?.fechaFin ? new Date(data.fechaFin).toLocaleDateString('es-MX') : '—'}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500" fontWeight="bold">RESPONSABLE</Text>
            <Text>{data?.responsable}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500" fontWeight="bold">% AVANCE</Text>
            <Text>{data?.porcentajeAvance}%</Text>
          </Box>
          {data?.rutaDocumento && (
            <Box gridColumn={{ md: 'span 2' }}>
              <Text fontSize="xs" color="gray.500" fontWeight="bold">DOCUMENTO PDF</Text>
              <HStack>
                <Icon as={FiFile} color="red.500" />
                <Text color="blue.500" textDecoration="underline" cursor="pointer"
                  onClick={() => window.open(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000'}${data.rutaDocumento}`, '_blank')}>
                  Ver documento
                </Text>
              </HStack>
            </Box>
          )}
        </SimpleGrid>
      </VStack>
    );
  }

  // Modo nuevo / editar
  return (
    <VStack spacing={4} align="stretch">
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Año</FormLabel>
          <Select value={anio} onChange={(e) => setAnio(e.target.value)}>
            {aniosDisponibles.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </Select>
        </FormControl>

        <FormControl isRequired>
          <FormLabel fontSize="sm">Estatus</FormLabel>
          <Select value={estatus} onChange={(e) => setEstatus(e.target.value)}>
            <option value="Borrador">Borrador</option>
            <option value="Activo">Activo</option>
            <option value="Concluido">Concluido</option>
          </Select>
        </FormControl>

        <FormControl isRequired gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Nombre del plan</FormLabel>
          <Input
            placeholder="Ej. Plan Anual de Cultura 2026"
            value={nombrePlan}
            onChange={(e) => setNombrePlan(e.target.value)}
          />
        </FormControl>

        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Objetivo general</FormLabel>
          <Textarea
            placeholder="Describe el objetivo general del plan..."
            value={objetivoGeneral}
            onChange={(e) => setObjetivoGeneral(e.target.value)}
            rows={3}
          />
        </FormControl>

        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Descripción</FormLabel>
          <Textarea
            placeholder="Descripción detallada del plan cultural..."
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            rows={3}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel fontSize="sm">Fecha inicio</FormLabel>
          <Input
            type="date"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel fontSize="sm">Fecha fin</FormLabel>
          <Input
            type="date"
            value={fechaFin}
            onChange={(e) => setFechaFin(e.target.value)}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel fontSize="sm">Responsable</FormLabel>
          <Input
            placeholder="Nombre del responsable"
            value={responsable}
            onChange={(e) => setResponsable(e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm">% Avance</FormLabel>
          <Input
            type="number"
            min={0}
            max={100}
            placeholder="0"
            value={porcentajeAvance}
            onChange={(e) => {
              const v = Math.min(100, Math.max(0, Number(e.target.value)));
              setPorcentajeAvance(v.toString());
            }}
          />
        </FormControl>

        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Documento PDF del plan</FormLabel>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            style={{ display: 'none' }}
            onChange={handleCambioArchivo}
          />
          <Box
            border="2px dashed"
            borderColor="gray.300"
            borderRadius="md"
            p={4}
            textAlign="center"
            cursor="pointer"
            _hover={{ borderColor: '#5b182b', bg: 'red.50' }}
            onClick={handleSeleccionarArchivo}
          >
            {nombreArchivoActual ? (
              <HStack justify="center">
                <Icon as={FiFile} color="red.500" />
                <Text fontSize="sm" color="gray.700">{nombreArchivoActual}</Text>
              </HStack>
            ) : (
              <VStack spacing={1}>
                <Icon as={FiUpload} boxSize={6} color="gray.400" />
                <Text fontSize="sm" color="gray.500">
                  Haz clic para seleccionar un archivo PDF
                </Text>
              </VStack>
            )}
          </Box>
        </FormControl>
      </SimpleGrid>

      <HStack justify="flex-end" pt={2} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>
          Cancelar
        </Button>
        <Button
          bg="#5b182b"
          color="white"
          _hover={{ bg: '#691f30' }}
          onClick={handleGuardar}
          isLoading={guardando}
          loadingText="Guardando..."
        >
          Guardar
        </Button>
      </HStack>
    </VStack>
  );
};

export default FormularioPlanCultura;
